import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Platform,
  ScrollView,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { ProductForm, ProductFormData } from '../components/products';
import { ProductImage } from '../components/products/ProductImageUpload';
import { ProductVendorsSection } from '../components/products/ProductVendorsSection';
import { DataListFieldRenderer } from '../components/data-lists/DataListFieldRenderer';
import { apiService, DataListColumn } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { colors, spacing, typography } from '../theme';
import { Button } from '../components/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ProductEditScreenProps {
  productId: string;
}

export const ProductEditScreen: React.FC<ProductEditScreenProps> = ({
  productId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const { currentBusiness } = useAppStore();
  // Set when this product was reached via a system Products Data List's
  // scoped mode (see ProductsListScreen.tsx) — redirects back into that
  // context on save/cancel instead of the generic Products screen. The
  // record is already a list member (added there originally), so no
  // linkDataListRecords call is needed here — unlike ProductCreateScreen's
  // handling of a brand-new record.
  const listId: string | undefined = routeParams?.listId;
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState<Partial<ProductFormData> | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [catalogueId, setCatalogueId] = useState<string>('');
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  // Extra fields (item 2) — only present when this product is linked to a
  // system Products Data List that has customColumns configured.
  const [customColumns, setCustomColumns] = useState<DataListColumn[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const [customValueErrors, setCustomValueErrors] = useState<Record<string, string>>({});
  const [savingCustomValues, setSavingCustomValues] = useState(false);

  useEffect(() => {
    if (!listId) return;
    apiService.getDataList(listId).then((res) => {
      if (res.statusCode === 1000 && res.data?.customColumns?.length) {
        setCustomColumns(res.data.customColumns);
      }
    }).catch((err) => console.error('[ProductEditScreen] Failed to load linked list:', err));
    apiService.getDataListRecord(listId, productId).then((res) => {
      if (res.statusCode === 1000 && res.data) {
        setCustomValues(res.data.values || {});
      }
    }).catch((err) => console.error('[ProductEditScreen] Failed to load custom field values:', err));
  }, [listId, productId]);

  const handleSaveCustomValues = async () => {
    if (!listId) return;
    try {
      setSavingCustomValues(true);
      const res = await apiService.upsertDataListRecordCustomValues(listId, productId, customValues);
      if (res.statusCode === 1000) {
        showNotification('success', 'Success', 'Extra fields updated successfully.');
        setCustomValueErrors({});
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to update extra fields');
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to update extra fields');
    } finally {
      setSavingCustomValues(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    try {
      setLoadingProduct(true);
      const response = await apiService.getProductById(productId);
      if (response.statusCode === 1000 && response.data) {
        const product = response.data;
        // Route-context catalogueId (used only for post-save navigation) stays
        // the first membership entry if any — a product opened via a specific
        // catalogue's product list should navigate back there on save.
        setCatalogueId(product.catalogueIds?.[0] || '');
        setVendorIds(Array.isArray((product as any).vendorIds) ? (product as any).vendorIds.map(String) : []);
        setInitialData({
          catalogueIds: product.catalogueIds || [],
          productType: product.productType || 'physical',
          name: product.name,
          description: product.description,
          brand: product.brand,
          sku: product.sku,
          barcode: product.barcode,
          price: product.price,
          currency: product.currency,
          salePrice: product.salePrice,
          costPrice: product.costPrice,
          inventoryQuantity: product.inventoryQuantity,
          inventoryTracking: product.inventoryTracking,
          lowStockThreshold: product.lowStockThreshold,
          minimumOrderQuantity: product.minimumOrderQuantity ?? null,
          images: product.images || [],
          categoryIds: product.categoryIds || [],
          hasVariants: product.hasVariants,
          variants: product.variants || [],
          availability: product.availability,
          condition: product.condition,
          weight: product.weight,
          dimensions: product.dimensions,
          unitId: product.unitId,
          priceUnit: product.priceUnit,
          digitalDelivery: product.digitalDelivery,
          serviceDelivery: product.serviceDelivery,
          serviceLocation: product.serviceLocation,
          addons: product.addons || [],
          pricingZones: product.pricingZones || [],
        });
      } else {
        showNotification('error', 'Error', 'Failed to load product');
        setTimeout(() => {
          if (Platform.OS === 'web') {
            window.history.back();
          } else {
            navigateTo('products');
          }
        }, 2000);
        return;
      }
    } catch (error: any) {
      console.error('Error loading product:', error);
      showNotification('error', 'Error', error.message || 'Failed to load product');
      setTimeout(() => {
        if (Platform.OS === 'web') {
          window.history.back();
        } else {
          navigateTo('products');
        }
      }, 2000);
      return;
    } finally {
      setLoadingProduct(false);
    }
  };

  const handleSubmit = async (formData: ProductFormData) => {
    try {
      setLoading(true);

      // Convert blob URLs and file URIs to base64 data URIs before sending
      const processedImages = await Promise.all(
        formData.images.map(async (img) => {
          // If already a Firebase Storage URL or other valid URL, use as-is
          if (!img.url.startsWith('blob:') && !img.url.startsWith('file://') && !img.url.startsWith('content://')) {
            return img;
          }

          // Convert blob URL or file URI to base64 data URI
          try {
            let blob: Blob;
            
            if (img.url.startsWith('blob:') && typeof window !== 'undefined') {
              // Fetch blob URL and convert to blob
              const response = await fetch(img.url);
              blob = await response.blob();
            } else {
              // For file:// or content:// URIs, we need to use React Native's FileSystem
              // For now, skip these and let them be uploaded separately after product update
              console.warn('[ProductEditScreen] File URI detected, will upload separately:', img.url);
              return img; // Return as-is, will be handled separately
            }

            // Convert blob to base64
            return new Promise<ProductImage>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64Data = reader.result as string;
                resolve({
                  ...img,
                  url: base64Data, // Replace blob URL with base64 data URI
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } catch (error: any) {
            console.error('[ProductEditScreen] Error converting image to base64:', error);
            // Return original image - backend will handle or skip it
            return img;
          }
        })
      );

      // Separate processed images (base64) from server URLs and file URIs that couldn't be converted
      const base64Images = processedImages.filter(img => img.url.startsWith('data:'));
      const serverImages = processedImages.filter(img => 
        !img.url.startsWith('data:') && !img.url.startsWith('blob:') && 
        !img.url.startsWith('file://') && !img.url.startsWith('content://')
      );
      const fileUriImages = processedImages.filter(img => 
        img.url.startsWith('file://') || img.url.startsWith('content://')
      );

      // Transform form data to API format
      // Include base64 images and server images (backend will upload base64 to Firebase Storage)
      const productData = {
        catalogueIds: formData.catalogueIds,
        productType: formData.productType,
        name: formData.name,
        description: formData.description,
        brand: formData.brand,
        sku: formData.sku,
        barcode: formData.barcode,
        price: formData.price,
        currency: formData.currency,
        salePrice: formData.salePrice,
        costPrice: formData.costPrice,
        inventoryQuantity: formData.inventoryQuantity,
        inventoryTracking: formData.inventoryTracking,
        lowStockThreshold: formData.lowStockThreshold,
        minimumOrderQuantity: formData.minimumOrderQuantity ?? null,
        images: [...base64Images, ...serverImages],
        categoryIds: formData.categoryIds,
        hasVariants: formData.hasVariants,
        variants: formData.variants,
        availability: formData.availability,
        condition: formData.condition,
        weight: formData.weight,
        dimensions: formData.dimensions,
        unitId: formData.unitId,
        priceUnit: formData.priceUnit,
        digitalDelivery: formData.digitalDelivery,
        serviceDelivery: formData.serviceDelivery,
        serviceLocation: formData.serviceLocation,
        addons: formData.addons,
        pricingZones: formData.pricingZones,
      };

      const response = await apiService.updateProduct(productId, productData);

      if (response.statusCode === 1000 && response.data) {
        showNotification('success', 'Success', 'Product updated successfully.');
        setTimeout(() => {
          if (listId) {
            navigateTo('data-list-records', listId);
          } else if (Platform.OS === 'web') {
            window.history.back();
          } else {
            navigateTo('products');
          }
        }, 1500);
      } else {
        const errorMessage =
          response.errors?.[0]?.message || 'Failed to update product';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error updating product:', error);
      showNotification('error', 'Error', error.message || 'Failed to update product');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (listId) {
      navigateTo('data-list-records', listId);
    } else if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('products');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loadingProduct) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        formHeader={<FormHeader title="Edit Product" onBack={handleCancel} />}
      >
        <View style={styles.screen}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={styles.loadingText}>Loading product...</Text>
          </View>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title={initialData?.name || 'Edit Product'} onBack={handleCancel} />}
    >
      <View style={styles.screen}>
        <ScrollView
        style={styles.container}
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' && styles.contentContainerWeb,
          Platform.OS !== 'web' && styles.contentContainerMobile
        ]}
        showsVerticalScrollIndicator={false}
      >
        {initialData && (
          <ProductForm
            initialData={initialData}
            catalogueId={catalogueId}
            productId={productId}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            submitLabel="Update Product"
            loading={loading}
          />
        )}

        {initialData && <ProductVendorsSection productId={productId} vendorIds={vendorIds} />}

        {/* Extra Fields (item 2) — custom columns on the linked system Data
            List, alongside this product's own fixed fields above. Only shown
            when reached via that list's scoped mode AND it has customColumns
            configured. */}
        {listId && customColumns.length > 0 && currentBusiness?._id && (
          <View style={styles.customFieldsSection}>
            <Text style={styles.customFieldsSectionTitle}>Extra Fields</Text>
            {customColumns.map((col) => (
              <DataListFieldRenderer
                key={col.id}
                column={col}
                value={customValues[col.id]}
                onChange={(v) => setCustomValues((prev) => ({ ...prev, [col.id]: v }))}
                error={customValueErrors[col.id]}
                businessId={currentBusiness._id}
                listId={listId}
                recordId={productId}
              />
            ))}
            <Button
              title="Save Extra Fields"
              onPress={handleSaveCustomValues}
              loading={savingCustomValues}
              variant="secondary"
            />
          </View>
        )}
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  screen: {
    flex: 1,
  },
  customFieldsSection: {
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border as string,
    backgroundColor: colors.background.surface as string,
  },
  customFieldsSectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600' as const,
    color: colors.text.primary as string,
    marginBottom: spacing.md,
  },
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100, // Extra padding for mobile to avoid bottom tabs overlay
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm, // Minimal padding on web (ScreenLayout handles spacing)
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});

